/**
 * WorkflowEngagementModal - Admin Operations Cockpit
 * 
 * A modal for viewing and operating on workflow engagements.
 * Shows milestone chart, next action, and provides action buttons for admin operations.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2,
  Circle,
  Clock,
  Lock,
  AlertCircle,
  Calendar,
  FileText,
  MessageSquare,
  Loader2,
  ExternalLink,
  RefreshCw,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Milestone status visual config
const milestoneStatusConfig = {
  DONE: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20", label: "Complete" },
  READY: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/20", label: "Ready" },
  IN_PROGRESS: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/20", label: "In Progress" },
  LOCKED: { icon: Lock, color: "text-slate-500", bg: "bg-slate-700/50", label: "Upcoming" },
  BLOCKED: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-500/20", label: "Blocked" }
};

/**
 * MilestoneChart - Visual milestone progress display
 */
const MilestoneChart = ({ milestones, onMarkDone }) => {
  return (
    <div className="space-y-2">
      {milestones.map((m, idx) => {
        const config = milestoneStatusConfig[m.status] || milestoneStatusConfig.LOCKED;
        const IconComponent = config.icon;
        const canMarkDone = m.status === "READY" || m.status === "IN_PROGRESS";
        
        return (
          <div 
            key={m.code}
            className={`flex items-center gap-3 p-3 rounded-lg border ${
              m.status === "DONE" 
                ? "bg-green-500/5 border-green-500/30" 
                : m.status === "READY" || m.status === "IN_PROGRESS"
                  ? "bg-blue-500/5 border-blue-500/30"
                  : "bg-slate-800/50 border-slate-700"
            }`}
          >
            <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center`}>
              <IconComponent className={`w-4 h-4 ${config.color}`} />
            </div>
            <div className="flex-1">
              <p className={`font-medium text-sm ${m.status === "LOCKED" ? "text-slate-500" : "text-white"}`}>
                {m.title}
              </p>
              {m.completed_at && (
                <p className="text-xs text-slate-500">
                  {new Date(m.completed_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`text-xs ${config.bg} ${config.color} border-0`}>
                {config.label}
              </Badge>
              {canMarkDone && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => onMarkDone(m.code)}
                >
                  Mark Done
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main Modal Component
 */
const WorkflowEngagementModal = ({ isOpen, onClose, serId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [engagement, setEngagement] = useState(null);
  const [events, setEvents] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Action form states
  const [schedulingForm, setSchedulingForm] = useState({ scheduled_time: "", meeting_link: "", notes: "" });
  const [transcriptForm, setTranscriptForm] = useState({ transcript_text: "", interview_number: 1, notes: "" });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchEngagement = useCallback(async () => {
    if (!serId || !token) return;
    setLoading(true);
    try {
      const [engRes, eventsRes, artifactsRes] = await Promise.all([
        axios.get(`${API}/workflow/admin/engagement/${serId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/workflow/admin/engagement/${serId}/events?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/workflow/admin/engagement/${serId}/artifacts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEngagement(engRes.data);
      setEvents(eventsRes.data.events || []);
      setArtifacts(artifactsRes.data.artifacts || []);
    } catch (err) {
      console.error("Failed to fetch engagement:", err);
      toast.error("Failed to load engagement details");
    } finally {
      setLoading(false);
    }
  }, [serId, token]);

  useEffect(() => {
    if (isOpen && serId) {
      fetchEngagement();
    }
  }, [isOpen, serId, fetchEngagement]);

  const handleAction = async (actionName, endpoint, body = {}) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/workflow/admin/engagement/${serId}/${endpoint}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${actionName} completed`);
      await fetchEngagement(); // Refresh data
    } catch (err) {
      console.error(`${actionName} failed:`, err);
      toast.error(err.response?.data?.detail || `${actionName} failed`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkMilestoneDone = async (code) => {
    await handleAction("Milestone update", "milestone", { code, status: "DONE" });
  };

  const handleScheduleDiscovery = async (e) => {
    e.preventDefault();
    await handleAction("Schedule discovery", "schedule-discovery", schedulingForm);
    setSchedulingForm({ scheduled_time: "", meeting_link: "", notes: "" });
  };

  const handleCompleteDiscovery = async () => {
    await handleAction("Complete discovery", "complete-discovery", {});
  };

  const handleGenerateQuestionnaire = async () => {
    await handleAction("Generate questionnaire", "generate-questionnaire", {});
  };

  const handleIngestTranscript = async (e) => {
    e.preventDefault();
    if (!transcriptForm.transcript_text.trim()) {
      toast.error("Transcript text is required");
      return;
    }
    await handleAction("Ingest transcript", "ingest-transcript", transcriptForm);
    setTranscriptForm({ transcript_text: "", interview_number: 1, notes: "" });
  };

  const handleGenerateReport = async () => {
    await handleAction("Generate report", "generate-final-report", {});
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-navy-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center justify-between">
            <span>Engagement Operations</span>
            <Button variant="ghost" size="sm" onClick={fetchEngagement} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : engagement ? (
          <div className="space-y-4">
            {/* Header Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-800/50 rounded-lg">
              <div>
                <p className="text-slate-400 text-xs">Client</p>
                <p className="text-white font-medium">{engagement.user_email}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Service</p>
                <p className="text-white font-medium">{engagement.service_name || engagement.service_public_id}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Payment</p>
                <Badge className={engagement.payment_status === "SUCCEEDED" 
                  ? "bg-green-500/20 text-green-400" 
                  : "bg-yellow-500/20 text-yellow-400"
                }>
                  {engagement.payment_status}
                </Badge>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Status</p>
                <Badge className="bg-blue-500/20 text-blue-400">
                  {engagement.engagement_status}
                </Badge>
              </div>
            </div>

            {/* Next Action Banner */}
            {engagement.next_action?.label && engagement.next_action.code !== "NONE" && (
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <p className="text-primary text-sm font-medium">Next Action Required:</p>
                <p className="text-white font-semibold">{engagement.next_action.label}</p>
                <p className="text-slate-400 text-sm">{engagement.next_action.instructions}</p>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-slate-800">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="milestones">Milestones</TabsTrigger>
                <TabsTrigger value="actions">Actions</TabsTrigger>
                <TabsTrigger value="artifacts">Artifacts</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Current Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Current Step</span>
                        <span className="text-white">{engagement.current_step?.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Milestones Completed</span>
                        <span className="text-white">
                          {engagement.milestones?.filter(m => m.status === "DONE").length || 0} / {engagement.milestones?.length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Discovery Call</span>
                        <span className="text-white">{engagement.discovery_call?.status || "NOT_SCHEDULED"}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Milestones Tab */}
              <TabsContent value="milestones">
                <MilestoneChart 
                  milestones={engagement.milestones || []} 
                  onMarkDone={handleMarkMilestoneDone}
                />
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="space-y-4">
                {/* Schedule Discovery */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      Schedule Discovery Call
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleScheduleDiscovery} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-400 text-xs">Date & Time</Label>
                          <Input
                            type="datetime-local"
                            value={schedulingForm.scheduled_time}
                            onChange={(e) => setSchedulingForm(f => ({ ...f, scheduled_time: e.target.value }))}
                            className="bg-slate-900 border-slate-700 text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">Meeting Link</Label>
                          <Input
                            type="url"
                            placeholder="https://zoom.us/j/..."
                            value={schedulingForm.meeting_link}
                            onChange={(e) => setSchedulingForm(f => ({ ...f, meeting_link: e.target.value }))}
                            className="bg-slate-900 border-slate-700 text-white"
                          />
                        </div>
                      </div>
                      <Button type="submit" disabled={actionLoading} size="sm">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Schedule Call
                      </Button>
                    </form>
                    {engagement.discovery_call?.status === "SCHEDULED" && (
                      <Button 
                        onClick={handleCompleteDiscovery} 
                        disabled={actionLoading} 
                        variant="outline"
                        size="sm"
                        className="mt-3"
                      >
                        Mark Call Completed
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    <Button onClick={handleGenerateQuestionnaire} disabled={actionLoading} variant="outline" size="sm">
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Questionnaire
                    </Button>
                    <Button onClick={handleGenerateReport} disabled={actionLoading} variant="outline" size="sm">
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Final Report
                    </Button>
                  </CardContent>
                </Card>

                {/* Ingest Transcript */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary" />
                      Ingest Transcript
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleIngestTranscript} className="space-y-3">
                      <div>
                        <Label className="text-slate-400 text-xs">Transcript Text</Label>
                        <textarea
                          value={transcriptForm.transcript_text}
                          onChange={(e) => setTranscriptForm(f => ({ ...f, transcript_text: e.target.value }))}
                          placeholder="Paste interview transcript here..."
                          className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-sm resize-none"
                          required
                        />
                      </div>
                      <Button type="submit" disabled={actionLoading} size="sm">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Ingest Transcript
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Artifacts Tab */}
              <TabsContent value="artifacts">
                <div className="space-y-2">
                  {artifacts.length > 0 ? artifacts.map((art) => (
                    <div 
                      key={art.artifact_id}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-white text-sm font-medium">{art.type}</p>
                          <p className="text-slate-500 text-xs">v{art.version} • {art.status}</p>
                        </div>
                      </div>
                      <Badge className={art.status === "GENERATED" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-slate-700 text-slate-400"
                      }>
                        {art.status}
                      </Badge>
                    </div>
                  )) : (
                    <p className="text-slate-500 text-center py-8">No artifacts yet</p>
                  )}
                </div>
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline">
                <div className="space-y-2">
                  {events.length > 0 ? events.map((evt) => (
                    <div 
                      key={evt.event_id}
                      className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{evt.type.replace(/_/g, ' ')}</p>
                        <p className="text-slate-500 text-xs">
                          {new Date(evt.created_at).toLocaleString()} • {evt.actor}
                        </p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-slate-500 text-center py-8">No events yet</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">Engagement not found</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowEngagementModal;
